import { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SaveButton from "components/buttons/SaveButton";
import CustomTable from "components/tables/CustomTable";
import DateField from "components/inputs/DateField";
import DropdownField from "components/inputs/DropdownField";
import ReportsAccordion from "components/accordions/ReportsAccordion";
import usePagination from "hooks/usePagination";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useGetReportMaintenanceOrganizations,
  useGetReportMaintenanceOrganizationCosts,
} from "hooks/queries/useCompanyReports";
import { useGetAllCompanies } from "hooks/queries/useCompanies";
import { formatDateTime } from "utils/formatDateTime";
import { formatNumbersWithSeparator } from "utils/formatNumbersWithSeperator";
import colors from "config/theme/colors";
import CopyButton from "components/buttons/CopyButton";
import dayjs from "dayjs";
import SpinningCircle from "components/spinners/SpinningCircle";
import CompanyReportsExportButtons from "./CompanyReportsExportButtons";
import DatePeriodSelector from "components/inputs/DatePeriodSelector";

const availableOrganizationReportTypeOptions = [
  { label: "Συντηρήσεις ανά Οργανισμό", value: "MaintenancesPerOrganization" },
  {
    label: "Συνολικές Δαπάνες Συντηρήσεων ανά Οργανισμό",
    value: "MaintenanceExpensesPerOrganization",
  },
];

const columns = {
  MaintenancesPerOrganization: [
    {
      id: "externalCompanyName",
      label: "Οργανισμός",
      isSortable: false,
    },
    { id: "assetName", label: "Ακίνητο", isSortable: false },
    { id: "kaek", label: "ΚΑΕΚ", isSortable: false },
    { id: "equipmentName", label: "Εξοπλισμός", isSortable: false },
    { id: "type", label: "Τύπος Συντήρησης", isSortable: false },
    {
      id: "maintenanceDate",
      label: "Ημερομηνία Συντήρησης",
      isSortable: false,
    },
    { id: "maintenanceCost", label: "Κόστος", isSortable: false },
  ],
  MaintenanceExpensesPerOrganization: [
    { id: "organizationName", label: "Όνομα Οργανισμού", isSortable: false },
    {
      id: "numberOfMaintenances",
      label: "Σύνολο Συντηρήσεων",
      isSortable: false,
    },
    { id: "totalCost", label: "Συνολικό Κόστος", isSortable: false },
    {
      id: "avergageCost",
      label: "Μέσο Κόστος Ανά Συντήρηση",
      isSortable: false,
    },
  ],
};

const getValidationSchema = (reportType) => {
  if (reportType === "MaintenancesPerOrganization") {
    return yup.object().shape({
      dateFrom: yup.date().required("Ημερομηνία από είναι υποχρεωτική"),
      dateTo: yup.date().required("Ημερομηνία έως είναι υποχρεωτική"),
      organization: yup
        .array()
        .of(yup.object().shape({ id: yup.string(), label: yup.string() }))
        .nullable(),
    });
  } else if (reportType === "MaintenanceExpensesPerOrganization") {
    return yup.object().shape({
      dateFrom: yup.date().required("Ημερομηνία από είναι υποχρεωτική"),
      dateTo: yup.date().required("Ημερομηνία έως είναι υποχρεωτική"),
    });
  } else {
    return yup.object().shape({});
  }
};

const renderKaek = (item) => {
  return (
    <Box
      px={2}
      borderRadius={2}
      bgcolor={colors.greyBackground}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography fontSize={12} fontWeight={700} color={colors.grey}>
        {item.kaek}
      </Typography>
      <CopyButton textToCopy={item.kaek} />
    </Box>
  );
};

const CompanyReports = () => {
  const [reportType, setReportType] = useState("");
  const [data, setData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    page,
    rowsPerPage,
    setPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination();
  const {
    data: organizations,
    isLoading: organizationsLoading,
    error: organizationsError,
  } = useGetAllCompanies(
    {
      pageNumber: -1,
      pageSize: -1,
    },
    { enabled: reportType === "MaintenancesPerOrganization" },
  );
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(getValidationSchema(reportType)),
  });

  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");
  const organization = watch("organization");

  const handleSelectPeriod = (startDate, endDate) => {
    setValue("dateFrom", startDate);
    setValue("dateTo", endDate);
  };

  const fetchedData = {
    MaintenancesPerOrganization: useGetReportMaintenanceOrganizations(
      {
        organizationId: organization?.map((o) => o.id) || null,
        dateFrom: dayjs(dateFrom).utc(true).format(),
        dateTo: dayjs(dateTo).utc(true).format(),
        pageNumber: page,
        pageSize: rowsPerPage,
      },
      { enabled: reportType === "MaintenancesPerOrganization" && isSubmitting },
    ),
    MaintenanceExpensesPerOrganization:
      useGetReportMaintenanceOrganizationCosts(
        {
          dateFrom: dayjs(dateFrom).utc(true).format(),
          dateTo: dayjs(dateTo).utc(true).format(),
          pageNumber: page,
          pageSize: rowsPerPage,
        },
        {
          enabled:
            reportType === "MaintenanceExpensesPerOrganization" && isSubmitting,
        },
      ),
  };

  useEffect(() => {
    if (isSubmitting) {
      const activeQuery = fetchedData[reportType];
      setData(activeQuery.data || {});
    }
  }, [fetchedData, reportType, isSubmitting]);

  const isLoading = fetchedData[reportType]?.isLoading;

  const formattedData = useMemo(
    () =>
      data?.items?.map((item, i) => {
        switch (reportType) {
          case "MaintenancesPerOrganization":
            return {
              id: `${item.maintenanceDate}-${item.kaek}-${i}`,
              assetName: item.assetName || "-",
              kaek: item.kaek ? renderKaek(item) : "-",
              equipmentName: item.equipmentName || "-",
              type: item.type || "-",
              maintenanceDate: formatDateTime(item.maintenanceDate, true),
              maintenanceCost: item.maintenanceCost
                ? `${formatNumbersWithSeparator(item.maintenanceCost, 2)}€`
                : "-",
              externalCompanyName: item.externalCompanyName || "-",
            };
          case "MaintenanceExpensesPerOrganization":
            return {
              id: `${item.organizationName}-${i}`,
              organizationName: item.organizationName || "-",
              numberOfMaintenances: item.numberOfMaintenances || "-",
              totalCost: item.totalCost
                ? `${formatNumbersWithSeparator(item.totalCost, 2)}€`
                : "-",
              avergageCost: item.avergageCost
                ? `${formatNumbersWithSeparator(item.avergageCost, 2)}€`
                : "-",
            };
          default:
            return item;
        }
      }),
    [data, reportType],
  );

  const organizationOptions = organizations?.items?.map((organization) => ({
    id: organization.id,
    label: organization.name,
  }));
  const totalResults = data?.totalCount ?? null;
  const companyReportTypeOptions = availableOrganizationReportTypeOptions.map(
    (option) => option.label,
  );

  const handleOnFilterChange = () => {
    setIsSubmitting(false);
    setPage(1);
  };

  const handleReportTypeChange = (event, value) => {
    const selectedReport = availableOrganizationReportTypeOptions.find(
      (option) => option.label === value,
    )?.value;
    setReportType(selectedReport || "");
    reset();
    handleOnFilterChange();
  };

  const onSubmit = () => {
    setIsSubmitting(true);
  };

  const getMinMaxDates = useCallback((reportType) => {
    const currentDate = dayjs().format("YYYY-MM-DD");
    const pastDate = dayjs().subtract(1, "year").format("YYYY-MM-DD");

    switch (reportType) {
      case "MaintenancesPerOrganization":
        return { min: pastDate, max: currentDate };
      case "MaintenanceExpensesPerOrganization":
        return { min: pastDate, max: currentDate };
      default:
        return { min: null, max: null };
    }
  }, []);

  const { min: dateFromMin, max: dateFromMax } = getMinMaxDates(reportType);
  const { min: dateToMin, max: dateToMax } = getMinMaxDates(reportType);

  if (organizationsLoading) {
    return <SpinningCircle />;
  }

  if (organizationsError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: "100%",
        }}
      >
        <p>Error: {organizationsError?.message}</p>
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: 4 }}>
      <Typography variant="h4" color="primary">
        Αναφορές Οργανισμών
      </Typography>
      <Box sx={{ marginTop: 3 }}>
        <DropdownField
          label="Επιλογή Κατηγορίας Αναφοράς"
          value={
            availableOrganizationReportTypeOptions.find(
              (option) => option.value === reportType,
            )?.label || ""
          }
          onChange={handleReportTypeChange}
          options={companyReportTypeOptions}
          required
        />
        <Box sx={{ marginTop: 3 }}>
          {availableOrganizationReportTypeOptions
            .map((option) => option.value)
            .includes(reportType) && (
            <ReportsAccordion
              sx={{
                marginTop: 2,
                width: "100%",
                padding: 1.7,
                borderRadius: 2,
                boxShadow: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
              title={`Φόρμα Αναφοράς ( ${
                availableOrganizationReportTypeOptions.find(
                  (option) => option.value === reportType,
                )?.label
              } )`}
            >
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ marginBottom: 4 }}>
                  <DatePeriodSelector
                    onSelectPeriod={handleSelectPeriod}
                    includePast={true}
                    includeFuture={false}
                  />
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <DateField
                    label="Ημερομηνία Από"
                    name="dateFrom"
                    control={control}
                    min={dateFromMin}
                    max={dateFromMax}
                    required
                    onChange={() => handleOnFilterChange()}
                  />
                  <DateField
                    label="Ημερομηνία Εώς"
                    name="dateTo"
                    control={control}
                    min={dateToMin}
                    max={dateToMax}
                    required
                    onChange={() => handleOnFilterChange()}
                  />
                  {reportType === "MaintenancesPerOrganization" && (
                    <DropdownField
                      label="Επιλογή Οργανισμού"
                      name="organization"
                      control={control}
                      options={organizationOptions}
                      multiple
                      onChange={() => handleOnFilterChange()}
                      sx={{
                        minWidth: 300,
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: 4,
                      marginLeft: 4,
                    }}
                  >
                    {Object.keys(errors).length > 0 && (
                      <Typography color="error" fontSize={14}>
                        Παρακαλώ ελέγξτε ότι όλα τα πεδία έχουν συμπληρωθεί
                        σωστά
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ marginTop: 3 }}>
                  <SaveButton text="Δημιουργία" isLoading={isLoading} />
                </Box>
              </form>
            </ReportsAccordion>
          )}
          <Box sx={{ marginTop: 3.5, display: "flex", alignItems: "center" }}>
            {totalResults > 0 && isSubmitting && (
              <>
                <Typography variant="h3" color="primary">
                  ({totalResults}) Αποτελέσματα
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  <CompanyReportsExportButtons
                    reportType={reportType}
                    reportLabel={
                      availableOrganizationReportTypeOptions.find(
                        (option) => option.value === reportType,
                      )?.label || ""
                    }
                    filters={{
                      dateFrom: dayjs(dateFrom).utc(true).format(),
                      dateTo: dayjs(dateTo).utc(true).format(),
                      organizationId: organization?.map((o) => o.id) || null,
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
          {totalResults > 0 && isSubmitting && (
            <Box
              sx={{
                marginTop: 3,
                marginBottom: 5,
                width: "100%",
                padding: 1.7,
                borderRadius: 2,
                boxShadow: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CustomTable
                columns={columns[reportType]}
                data={formattedData}
                page={page - 1}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                totalResults={totalResults}
                disableClick
                maxHeight={650}
                textWeight={500}
              />
            </Box>
          )}

          {totalResults === 0 && isSubmitting && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                height: "100%",
                marginTop: 2,
              }}
            >
              <Typography variant="h4">Δεν υπάρχουν αναφορές</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyReports;
